import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { FaSearch, FaVideo, FaChartLine, FaMobileAlt, FaUsers, FaPlay, FaThumbsUp, FaEye, FaCalendarAlt, FaCog, FaRocket, FaMoneyBillWave, FaShieldAlt, FaGoogle, FaYoutube, FaFilm, FaEdit, Hashtag, FaTags, FaLink, FaComments } from 'react-icons/fa';
import Home from '../Home';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Arial', sans-serif;
  }
  
  body {
    background-color: #f8f9fa;
    line-height: 1.6;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #003b94 0%, #003b94 100%);
  color: white;
  padding: 80px 0;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 20px;
  }
`;

const Section = styled.section`
  padding: 80px 0;
  
  &:nth-child(even) {
    background-color: white;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 50px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
`;

const BenefitCard = styled.div`
  background: white;
  padding: 40px 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  text-align: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const BenefitIcon = styled.div`
  font-size: 3rem;
  color: #FF0000;
  margin-bottom: 20px;
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 15px;
`;

const BenefitDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 50px;
`;

const StatCard = styled.div`
  text-align: center;
  padding: 30px;
  background: linear-gradient(135deg, #FF0000 0%, #CC0000 100%);
  color: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 50px;
`;

const ServiceCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  display: flex;
  align-items: flex-start;
  gap: 20px;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ServiceIcon = styled.div`
  font-size: 2.5rem;
  color: #FF0000;
  min-width: 60px;
`;

const ServiceContent = styled.div`
  flex: 1;
`;

const ServiceTitle = styled.h3`
  font-size: 1.4rem;
  color: #2c3e50;
  margin-bottom: 10px;
`;

const ServiceDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const PricingSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 40px 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  text-align: center;
  position: relative;
  transition: transform 0.3s ease;
  border: ${props => props.featured ? '3px solid #003b94' : '1px solid #e0e0e0'};
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: #003b94;
  color: white;
  padding: 5px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const PlanName = styled.h3`
  font-size: 1.8rem;
  color: #2c3e50;
  margin-bottom: 10px;
`;

const PlanPrice = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: #FF0000;
  margin: 20px 0;
  
  span {
    font-size: 1rem;
    color: #666;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 30px 0;
  text-align: left;
`;

const FeatureItem = styled.li`
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #666;
  
  &:before {
    content: '✓';
    color: #4caf50;
    font-weight: bold;
    margin-right: 10px;
  }
`;

const CTAButton = styled.button`
  background: ${props => props.primary ? '#003b94' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#003b94'};
  border: 2px solid #003b94;
  padding: 15px 30px;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    background: ${props => props.primary ? '#003b94' : '#003b94'};
    color: white;
  }
`;

const TestimonialSection = styled.div`
  background: linear-gradient(135deg, #003b94 0%, #003b94 100%);
  color: white;
  padding: 60px 0;
  text-align: center;
`;

const Testimonial = styled.div`
  max-width: 800px;
  margin: 0 auto;
  font-style: italic;
  font-size: 1.2rem;
  line-height: 1.8;
`;

const ClientName = styled.div`
  margin-top: 20px;
  font-weight: 600;
  font-size: 1.1rem;
`;

const ProcessSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 50px;
`;

const ProcessStep = styled.div`
  text-align: center;
  padding: 30px;
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  background: #003b94;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 auto 20px;
`;

const StepTitle = styled.h4`
  font-size: 1.3rem;
  color: #2c3e50;
  margin-bottom: 15px;
`;

const StepDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const Footer = styled.footer`
  background: #2c3e50;
  color: white;
  text-align: center;
  padding: 40px 0;
`;

const SEOYoutube = () => {
  return (
    <Home>
      <GlobalStyle />
      
      <Header>
        <Container>
          <HeaderTitle>SEO & YouTube Video Marketing Services</HeaderTitle>
          <HeaderSubtitle>
            Dominate search results and YouTube rankings with our comprehensive SEO and video marketing strategies. 
            Increase visibility, drive traffic, and grow your audience.
          </HeaderSubtitle>
        </Container>
      </Header>

      <Section>
        <Container>
          <SectionTitle>Why SEO & YouTube Marketing Work Together</SectionTitle>
          <p style={{textAlign: 'center', fontSize: '1.2rem', color: '#666', maxWidth: '800px', margin: '0 auto'}}>
            YouTube is the world's second largest search engine. Combining SEO with video marketing creates a powerful 
            synergy that can dramatically increase your online visibility and engagement.
          </p>
          
          <BenefitsGrid>
            <BenefitCard>
              <BenefitIcon><FaSearch /></BenefitIcon>
              <BenefitTitle>Dual Search Engine Dominance</BenefitTitle>
              <BenefitDescription>
                Rank on both Google and YouTube search results. Videos often appear in Google's top results, 
                giving you double the visibility for your target keywords.
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon><FaVideo /></BenefitIcon>
              <BenefitTitle>Higher Engagement Rates</BenefitTitle>
              <BenefitDescription>
                Video content generates 1200% more shares than text and images combined. 
                Keep visitors on your site longer and reduce bounce rates.
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon><FaChartLine /></BenefitIcon>
              <BenefitTitle>Improved Conversion Rates</BenefitTitle>
              <BenefitDescription>
                Websites with video can increase conversion rates by 80%. 
                Video builds trust and demonstrates your expertise effectively.
              </BenefitDescription>
            </BenefitCard>
          </BenefitsGrid>
        </Container>
      </Section>

      <Section style={{backgroundColor: '#f8f9fa'}}>
        <Container>
          <SectionTitle>Our Comprehensive SEO & YouTube Services</SectionTitle>
          <p style={{textAlign: 'center', color: '#666', marginBottom: '50px', maxWidth: '800px', margin: '0 auto 50px'}}>
            We offer integrated SEO and YouTube marketing services to maximize your online presence across all platforms.
          </p>
          
          <ServicesGrid>
            <ServiceCard>
              <ServiceIcon><FaGoogle /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Technical SEO Optimization</ServiceTitle>
                <ServiceDescription>
                  Comprehensive website audit, speed optimization, mobile responsiveness, 
                  schema markup implementation, and site structure optimization for better crawlability.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaEdit /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Content SEO Strategy</ServiceTitle>
                <ServiceDescription>
                  Keyword research, content planning, on-page optimization, meta tags optimization, 
                  and content creation designed to rank for your target keywords.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaLink /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Link Building & Authority Building</ServiceTitle>
                <ServiceDescription>
                  Strategic link acquisition, guest posting, digital PR, and authority building 
                  to improve your domain authority and search rankings.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaYoutube /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>YouTube Channel Optimization</ServiceTitle>
                <ServiceDescription>
                  Complete YouTube channel setup, branding, banner design, about section optimization, 
                  and channel trailer creation for maximum subscriber growth.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaFilm /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Video SEO & Optimization</ServiceTitle>
                <ServiceDescription>
                  YouTube video optimization including titles, descriptions, tags, custom thumbnails, 
                  cards, end screens, and transcript optimization for better rankings.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaTags /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Keyword Research for Video</ServiceTitle>
                <ServiceDescription>
                  Specialized keyword research for YouTube focusing on high-volume, low-competition 
                  keywords that drive targeted traffic to your videos.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaPlay /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Video Content Strategy</ServiceTitle>
                <ServiceDescription>
                  Develop a comprehensive video content calendar, topic research, script writing, 
                  and production planning aligned with your SEO goals.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaComments /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Audience Engagement Management</ServiceTitle>
                <ServiceDescription>
                  Comment management, community building, audience interaction strategies, 
                  and engagement optimization to boost YouTube algorithm performance.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaChartLine /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Analytics & Performance Tracking</ServiceTitle>
                <ServiceDescription>
                  Comprehensive tracking of SEO and YouTube metrics, performance reports, 
                  competitor analysis, and data-driven strategy adjustments.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaMobileAlt /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Local SEO & Video Integration</ServiceTitle>
                <ServiceDescription>
                  Combine local SEO strategies with video marketing to dominate local search results 
                  and attract customers in your geographic area.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaShieldAlt /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>E-E-A-T Optimization</ServiceTitle>
                <ServiceDescription>
                  Enhance Experience, Expertise, Authoritativeness, and Trustworthiness signals 
                  for both website content and video content to improve rankings.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon><FaRocket /></ServiceIcon>
              <ServiceContent>
                <ServiceTitle>Video Embedding & Backlink Strategy</ServiceTitle>
                <ServiceDescription>
                  Strategic video embedding on high-authority websites, video submission to directories, 
                  and leveraging video content for quality backlink acquisition.
                </ServiceDescription>
              </ServiceContent>
            </ServiceCard>
          </ServicesGrid>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionTitle>Proven Results</SectionTitle>
          <StatsSection>
            <StatCard>
              <StatNumber>85%</StatNumber>
              <StatLabel>Increase in Organic Traffic</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>3.2x</StatNumber>
              <StatLabel>Higher Search Visibility</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>67%</StatNumber>
              <StatLabel>More Video Views</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>45%</StatNumber>
              <StatLabel>Higher Conversion Rate</StatLabel>
            </StatCard>
          </StatsSection>
        </Container>
      </Section>

      <Section style={{backgroundColor: '#f8f9fa'}}>
        <Container>
          <SectionTitle>Our 4-Step Process</SectionTitle>
          <ProcessSection>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepTitle>Discovery & Analysis</StepTitle>
              <StepDescription>
                Comprehensive audit of your current SEO and video presence, competitor analysis, 
                and goal setting for your campaign.
              </StepDescription>
            </ProcessStep>
            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepTitle>Strategy Development</StepTitle>
              <StepDescription>
                Create customized SEO and video marketing strategies, keyword mapping, 
                content planning, and technical implementation roadmap.
              </StepDescription>
            </ProcessStep>
            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepTitle>Implementation & Optimization</StepTitle>
              <StepDescription>
                Execute the strategy with ongoing optimization, content creation, 
                video production, and continuous performance monitoring.
              </StepDescription>
            </ProcessStep>
            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepTitle>Reporting & Scaling</StepTitle>
              <StepDescription>
                Regular performance reporting, analysis of results, and scaling successful 
                strategies for continuous growth and improvement.
              </StepDescription>
            </ProcessStep>
          </ProcessSection>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionTitle>SEO & YouTube Marketing Packages</SectionTitle>
          <p style={{textAlign: 'center', color: '#666', marginBottom: '50px'}}>
            Choose the package that best fits your business needs. All plans include integrated SEO and YouTube strategies.
          </p>
          
          <PricingSection>
            <PricingCard>
              <PlanName>Starter Package</PlanName>
              <PlanPrice>₹499<span>/month</span></PlanPrice>
              <FeatureList>
                <FeatureItem>Basic SEO Audit & Optimization</FeatureItem>
                <FeatureItem>YouTube Channel Setup</FeatureItem>
                <FeatureItem>5 Keyword Optimization</FeatureItem>
                <FeatureItem>2 Video Optimizations/Month</FeatureItem>
                <FeatureItem>Monthly Performance Report</FeatureItem>
                <FeatureItem>Email Support</FeatureItem>
              </FeatureList>
              <CTAButton>Get Started</CTAButton>
            </PricingCard>
            
            <PricingCard featured>
              <PopularBadge>MOST POPULAR</PopularBadge>
              <PlanName>Professional Package</PlanName>
              <PlanPrice>₹999<span>/month</span></PlanPrice>
              <FeatureList>
                <FeatureItem>Everything in Starter</FeatureItem>
                <FeatureItem>Advanced Technical SEO</FeatureItem>
                <FeatureItem>15 Keyword Optimization</FeatureItem>
                <FeatureItem>5 Video Optimizations/Month</FeatureItem>
                <FeatureItem>Content Strategy Development</FeatureItem>
                <FeatureItem>Basic Link Building</FeatureItem>
                <FeatureItem>Weekly Performance Monitoring</FeatureItem>
                <FeatureItem>Priority Support</FeatureItem>
              </FeatureList>
              <CTAButton primary>Get Started</CTAButton>
            </PricingCard>
            
            <PricingCard>
              <PlanName>Enterprise Package</PlanName>
              <PlanPrice>₹1,999<span>/month</span></PlanPrice>
              <FeatureList>
                <FeatureItem>Everything in Professional</FeatureItem>
                <FeatureItem>Comprehensive SEO Strategy</FeatureItem>
                <FeatureItem>Unlimited Keyword Optimization</FeatureItem>
                <FeatureItem>Full Video Marketing Campaign</FeatureItem>
                <FeatureItem>Advanced Link Building</FeatureItem>
                <FeatureItem>Competitor Analysis</FeatureItem>
                <FeatureItem>Custom Video Production</FeatureItem>
                <FeatureItem>Dedicated Account Manager</FeatureItem>
                <FeatureItem>24/7 Premium Support</FeatureItem>
              </FeatureList>
              <CTAButton>Contact Sales</CTAButton>
            </PricingCard>
          </PricingSection>
        </Container>
      </Section>

      <TestimonialSection>
        <Container>
          <SectionTitle style={{color: 'white'}}>Success Stories</SectionTitle>
          <Testimonial>
            "After implementing their integrated SEO and YouTube strategy, our organic traffic increased by 150% in 4 months. 
            Our videos now rank on the first page of Google for competitive keywords, driving qualified leads daily."
          </Testimonial>
          <ClientName>Michael Chen, Digital Marketing Director</ClientName>
        </Container>
      </TestimonialSection>

      <Section style={{backgroundColor: '#f8f9fa'}}>
        <Container>
          <SectionTitle>Why Choose Our SEO & YouTube Services?</SectionTitle>
          <BenefitsGrid>
            <BenefitCard>
              <BenefitIcon><FaCog /></BenefitIcon>
              <BenefitTitle>Integrated Approach</BenefitTitle>
              <BenefitDescription>
                We don't treat SEO and YouTube as separate strategies. Our integrated approach ensures 
                both work together synergistically for maximum impact.
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon><FaMoneyBillWave /></BenefitIcon>
              <BenefitTitle>Proven ROI</BenefitTitle>
              <BenefitDescription>
                Our strategies are designed to deliver measurable results and clear return on investment 
                through increased traffic, leads, and conversions.
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon><FaRocket /></BenefitIcon>
              <BenefitTitle>Stay Ahead of Algorithms</BenefitTitle>
              <BenefitDescription>
                We continuously monitor search engine and YouTube algorithm updates to ensure 
                your strategies remain effective and compliant with latest best practices.
              </BenefitDescription>
            </BenefitCard>
          </BenefitsGrid>
        </Container>
      </Section>

     
    </Home>
  );
};

export default SEOYoutube;

